import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import Select from "react-select";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import appConfig from "../../config/appConfig";
import { ERROR_401 } from "../../config/errorPage";
import { formatSelectOption } from "../../helpers/function";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { AUTH_TOKEN } from "../../constants/token";
import InputRequire from "../components/InputRequire";
import { ToastBottomEnd } from "../components/Toast";
import {
  toastErrorMessageWithParameterConfig,
  toastSuccessMessageWithParameterConfig,
} from "../../config/toastConfig";

const MatrixImportPage = (props) => {
  const { t, ready } = useTranslation();

  document.title =
    "Satis client - " + (ready ? t("Import Matrice") : "");

  const userRoles = props.userRoles ?? [];

  const isHoldingUser = useMemo(
    () => userRoles.includes("admin-holding"),
    [userRoles]
  );

  const isFilialUser = useMemo(
    () =>
      userRoles.includes("admin-filial") || userRoles.includes("pilot-filial"),
    [userRoles]
  );

  const isAllowed = isHoldingUser || isFilialUser;

  if (!isAllowed) {
    window.location.href = ERROR_401;
  }

  const defaultError = {
    file: [],
    institution_id: [],
  };

  const [file, setFile] = useState("");
  const [fileName, setFileName] = useState("");
  const [institution, setInstitution] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [error, setError] = useState(defaultError);
  const [startRequest, setStartRequest] = useState(false);

  useEffect(() => {
    if (!isHoldingUser || !verifyTokenExpire()) return;

    axios
      .get(`${appConfig.apiDomaine}/my/institutions-whithout-holding`)
      .then((response) => {
        setInstitutions(
          formatSelectOption(response.data.institution, "name", false)
        );
      })
      .catch((err) => console.error(err));
  }, [isHoldingUser]);

  const handleChangeFile = (e) => {
    setFileName(e.target.value);
    setFile(Object.values(e.target.files)[0] ?? "");
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const targetInstitutionId = isHoldingUser
      ? institution?.value ?? null
      : props.userInstitutionId ?? null;

    if (isHoldingUser && !targetInstitutionId) {
      setError({
        ...defaultError,
        institution_id: [t("Veuillez sélectionner la filiale concernée.")],
      });
      return;
    }

    if (!file) {
      setError({
        ...defaultError,
        file: [t("Veuillez choisir le fichier HTML.")],
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    if (targetInstitutionId) {
      formData.append("institution_id", targetInstitutionId);
    }

    setStartRequest(true);
    setError(defaultError);
    axios.defaults.headers.common["Authorization"] = AUTH_TOKEN;

    if (verifyTokenExpire()) {
      await axios
        .post(`${appConfig.apiDomaine}/api/custom-guide`, formData)
        .then(() => {
          setStartRequest(false);
          setFile("");
          setFileName("");
          setInstitution(null);
          ToastBottomEnd.fire(
            toastSuccessMessageWithParameterConfig(
              t("Succès de l'importation")
            )
          );
        })
        .catch(({ response }) => {
          setStartRequest(false);
          if (response?.data?.error) {
            setError({ ...defaultError, ...response.data.error });
          }
          ToastBottomEnd.fire(
            toastErrorMessageWithParameterConfig(t("Echec de l'importation"))
          );
        });
    } else {
      setStartRequest(false);
    }
  };

  if (!ready) return null;

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Paramètre")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#icone" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <a
                href="#button"
                onClick={(e) => e.preventDefault()}
                className="kt-subheader__breadcrumbs-link"
                style={{ cursor: "text" }}
              >
                {t("Import Matrice")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <div className="row">
          <div className="col">
            <div className="kt-portlet">
              <div className="kt-portlet__head">
                <div className="kt-portlet__head-label">
                  <h3 className="kt-portlet__head-title">
                    {t("Importation de la matrice d'assistance (fichier HTML)")}
                  </h3>
                </div>
              </div>

              <form method="POST" className="kt-form">
                <div className="kt-form kt-form--label-right">
                  <div className="kt-portlet__body">
                    {isHoldingUser ? (
                      <div
                        className={
                          error.institution_id.length
                            ? "form-group row validated"
                            : "form-group row"
                        }
                      >
                        <label
                          className="col-xl-3 col-lg-3 col-form-label"
                          htmlFor="institution"
                        >
                          {t("Filiale concernée")} <InputRequire />
                        </label>
                        <div className="col-lg-9 col-xl-6">
                          <Select
                            inputId="institution"
                            placeholder={t("Veuillez sélectionner la filiale")}
                            value={institution}
                            onChange={setInstitution}
                            options={institutions}
                          />
                          {error.institution_id.length
                            ? error.institution_id.map((err, index) => (
                                <div
                                  key={index}
                                  className="invalid-feedback d-block"
                                >
                                  {err}
                                </div>
                              ))
                            : null}
                        </div>
                      </div>
                    ) : null}

                    <div
                      className={
                        error.file.length
                          ? "form-group row validated"
                          : "form-group row"
                      }
                    >
                      <label
                        className="col-xl-3 col-lg-3 col-form-label"
                        htmlFor="matrixFile"
                      >
                        {t("Fichier HTML")} <InputRequire />
                      </label>
                      <div className="col-lg-9 col-xl-6">
                        <input
                          id="matrixFile"
                          type="file"
                          accept=".html,.htm,text/html"
                          className={
                            error.file.length
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder={t("Veuillez choisir le fichier")}
                          value={fileName}
                          onChange={handleChangeFile}
                        />
                        {error.file.length
                          ? error.file.map((err, index) => (
                              <div key={index} className="invalid-feedback">
                                {err}
                              </div>
                            ))
                          : null}
                      </div>
                    </div>
                  </div>
                  <div className="kt-portlet__foot">
                    <div className="kt-form__actions text-right">
                      {!startRequest ? (
                        <button
                          type="submit"
                          onClick={onSubmit}
                          className="btn btn-primary"
                        >
                          {t("Enregistrer")}
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary kt-spinner kt-spinner--left kt-spinner--md kt-spinner--light"
                          type="button"
                          disabled
                        >
                          {t("Chargement")}...
                        </button>
                      )}

                      <Link
                        to="/assistance/matrix"
                        className="btn btn-secondary mx-2"
                      >
                        {t("Quitter")}
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const user = state.user.user ?? {};

  return {
    userPermissions: user.permissions,
    userRoles:
      user.roles?.map((role) => role.name) ??
      user.data?.roles?.map((role) => role.name) ??
      [],
    userInstitutionId:
      user.institution?.id ?? user.data?.identite?.staff?.institution?.id,
  };
};

export default connect(mapStateToProps)(MatrixImportPage);
